import { LayoutVersion } from '../types';
import {
  REPORT_LAB_COMPACT_LEGACY_GRID_SIZE_RATIO,
  REPORT_LAB_COMPACT_MODERN_GRID_SIZE_RATIO,
  REPORT_LAB_DEFAULT_LEGACY_GRID_SIZE_RATIO,
  REPORT_LAB_DEFAULT_MODERN_GRID_SIZE_RATIO,
  STUDIO_COMPACT_GRID_SIZE_RATIO,
  STUDIO_COMPACT_TABLE_DATA,
  STUDIO_DEFAULT_GRID_SIZE_RATIO,
  STUDIO_FONT_DEFAULTS,
} from './constants';
import { isReport, pxToPt, remToPt, type CustomBlockTypeEnum, supportsCompactMode } from 'venn-utils';
import type { AnalysisViewTypeEnum, InfoGraphicTypeEnum, PrintOrientationTypeEnum } from 'venn-api';
import { SCALE_FACTOR } from './blockFonts';
import type { blockFontKeys } from './types';
import type { DataGridSizeType } from 'venn-components';

/** Legacy scaled by 0.7 but Modern doesn't, so we scale by 0.7 here to compensate. */
const LEGACY_TO_MODERN = SCALE_FACTOR;

export const oldBlockConfigToFontSizeMappers = {
  blockTitle: createOldBlockMapper({
    // Values derived from the Headline element in BlockHeader.
    defaultLegacy: pxToPt(22) * LEGACY_TO_MODERN,
    defaultModern: remToPt(1.225),
    compactLegacy: pxToPt(22) * LEGACY_TO_MODERN,
    compactModern: remToPt(1.225),
    studioCompact: STUDIO_FONT_DEFAULTS.blockTitle.fontSizePt,
    studioDefault: STUDIO_FONT_DEFAULTS.blockTitle.fontSizePt,
  }),
  blockSubtitle: createOldBlockMapper({
    // studio and report lab originally used the Body1 typography element which is 12px on print
    defaultLegacy: pxToPt(12) * LEGACY_TO_MODERN,
    defaultModern: pxToPt(12),
    compactLegacy: pxToPt(12) * LEGACY_TO_MODERN,
    compactModern: pxToPt(12),
    studioCompact: STUDIO_FONT_DEFAULTS.blockSubtitle.fontSizePt,
    studioDefault: STUDIO_FONT_DEFAULTS.blockSubtitle.fontSizePt,
  }),
  blockTableData: (props) => {
    const fontSizes = {
      defaultLegacy: pxToPt(12) * LEGACY_TO_MODERN,
      defaultModern: remToPt(8 / 9),
      compactLegacy: pxToPt(9) * LEGACY_TO_MODERN,
      compactModern: remToPt(0.525),
      studioCompact: STUDIO_COMPACT_TABLE_DATA, // Compact table data is uniquely sized
      studioDefault: STUDIO_FONT_DEFAULTS.blockTableData.fontSizePt,
    };

    if (props.blockType === 'CORRELATION' && props.orientation === 'LANDSCAPE') {
      return createOldBlockMapper({
        defaultLegacy: pxToPt(10) * LEGACY_TO_MODERN,
        defaultModern: pxToPt(10),
        studioDefault: pxToPt(10),
        // Correlation block doesn't support compact, so the compact modes don't matter
        compactLegacy: 0,
        compactModern: 0,
        studioCompact: 0,
      })(props);
    }

    // For charts, we ignore compact mode. We've never used compact mode for determining font sizes in charts so we should ignore it now during mapping.
    if (!supportsCompactMode(props.infographicType, props.blockType)) {
      return createOldBlockMapper(fontSizes)({ ...props, dataGridSizeType: 'DEFAULT' as DataGridSizeType });
    }

    return createOldBlockMapper(fontSizes)(props);
  },
  blockHeaderInfo: createOldBlockMapper({
    defaultLegacy: pxToPt(10) * LEGACY_TO_MODERN,
    defaultModern: 8,
    compactLegacy: pxToPt(10) * LEGACY_TO_MODERN,
    compactModern: 8,
    studioCompact: STUDIO_FONT_DEFAULTS.blockHeaderInfo.fontSizePt,
    studioDefault: STUDIO_FONT_DEFAULTS.blockHeaderInfo.fontSizePt,
  }),
  blockChartAxis: createOldBlockMapper({
    defaultLegacy: pxToPt(10) * LEGACY_TO_MODERN,
    defaultModern: 8,
    compactLegacy: pxToPt(10) * LEGACY_TO_MODERN,
    compactModern: 8,
    studioCompact: STUDIO_FONT_DEFAULTS.blockChartAxis.fontSizePt,
    studioDefault: STUDIO_FONT_DEFAULTS.blockChartAxis.fontSizePt,
  }),
  blockChartLegend: createOldBlockMapper({
    defaultLegacy: pxToPt(12) * LEGACY_TO_MODERN,
    defaultModern: remToPt(8 / 9),
    compactLegacy: pxToPt(12) * LEGACY_TO_MODERN,
    compactModern: remToPt(8 / 9),
    studioCompact: STUDIO_FONT_DEFAULTS.blockChartLegend.fontSizePt,
    studioDefault: STUDIO_FONT_DEFAULTS.blockChartLegend.fontSizePt,
  }),
} satisfies Record<(typeof blockFontKeys)[number], MapperFn>;

export const mapOldConfigToTableSpacingRatio = createOldBlockMapper({
  defaultLegacy: REPORT_LAB_DEFAULT_LEGACY_GRID_SIZE_RATIO,
  defaultModern: REPORT_LAB_DEFAULT_MODERN_GRID_SIZE_RATIO,
  compactLegacy: REPORT_LAB_COMPACT_LEGACY_GRID_SIZE_RATIO,
  compactModern: REPORT_LAB_COMPACT_MODERN_GRID_SIZE_RATIO,
  studioCompact: STUDIO_COMPACT_GRID_SIZE_RATIO,
  studioDefault: STUDIO_DEFAULT_GRID_SIZE_RATIO,
});

type MapperFn = (props: {
  analysisViewType: AnalysisViewTypeEnum | undefined;
  layoutVersion: LayoutVersion;
  dataGridSizeType: DataGridSizeType | undefined;
  blockType: CustomBlockTypeEnum;
  infographicType: InfoGraphicTypeEnum;
  orientation: PrintOrientationTypeEnum;
}) => number;

function createOldBlockMapper(ptSizes: {
  defaultLegacy: number;
  defaultModern: number;
  compactLegacy: number;
  compactModern: number;
  studioCompact: number;
  studioDefault: number;
}): MapperFn {
  return (props) => {
    const { analysisViewType, layoutVersion, dataGridSizeType } = props;

    const isReportLab = analysisViewType && isReport(analysisViewType);
    const isCompact = dataGridSizeType === 'COMPACT';

    if (isReportLab) {
      if (layoutVersion === LayoutVersion.LEGACY) {
        return isCompact ? ptSizes.compactLegacy : ptSizes.defaultLegacy;
      }
      return isCompact ? ptSizes.compactModern : ptSizes.defaultModern;
    }
    return isCompact ? ptSizes.studioCompact : ptSizes.studioDefault;
  };
}
